import { FormFieldSchema, IInputFieldProps, IValidation } from "@generation/core";
import { InputProps } from "@shared/common";
import * as React from "react";
import { FieldValues, RefCallBack, useController, useFormContext } from "react-hook-form";
import { convertCallbackToValidationArray } from "../Utils/validate";
import { IsRequiredValidation } from "../Validations/IsRequiredValidation";
import { mapValidationsToRecord } from "../Validations/ValidationHelper";
import { IUseDependsOnHookResult, useDependsOnHook } from "./useDependsOnHook";

export interface IRegisterFieldHoc extends IInputFieldProps<FormFieldSchema> {
  currentValue?: string;
  input?: InputProps;
}

export function withRegisterFieldHoc<T extends IRegisterFieldHoc>(Component: React.FunctionComponent<T>) {
  const component = (props: T) => {
    props.field.validate = convertCallbackToValidationArray(props.field.validate);

    let result: IUseDependsOnHookResult = {};

    const formFieldName = props.name.substring(0, props.name.lastIndexOf(".")) ?? "";
    result = useDependsOnHook(formFieldName, props.field.dependsOn);

    if (result.isRequired !== undefined) {
      if (result.isRequired === true) {
        // eslint-disable-next-line no-param-reassign
        props.field.validate = [
          ...(!(props.field.validate ?? []).find((item) => item instanceof IsRequiredValidation)
            ? [new IsRequiredValidation(true)]
            : []),
          ...(props.field.validate ?? [])
        ];
      } else {
        // eslint-disable-next-line no-param-reassign
        props.field.validate = [
          ...(props.field.validate ?? []).filter(
            (item) =>
              !(item instanceof IsRequiredValidation) ||
              (item instanceof IsRequiredValidation && item.dependsOn === false)
          )
        ];
      }
    }

    props.field.validate = removeValidations(props.field.validate, result.removeValidations);
    props.field.validate = addValidations(props.field.validate, result.addValidations);

    let inputProps: {
      onChange: (event: unknown) => void;
      onBlur: (event: unknown) => void;
      value?: unknown;
      name: string;
    };
    let ref: RefCallBack;

    let isTouched: boolean | undefined;
    let isDirty: boolean | undefined;
    let isInvalid: boolean | undefined;

    if (props.field.type === "file" || props.field.type === "file-management") {
      const { register } = useFormContext();
      const field = register(props.name, {
        validate: {
          ...mapValidationsToRecord(formFieldName, props.field.validate)
        }
      });

      inputProps = field;
      ref = field.ref;
    } else {
      const { getValues } = useFormContext();
      const { field, fieldState } = useController<FieldValues, string>({
        name: props.name,
        rules: {
          validate: {
            ...mapValidationsToRecord(formFieldName, props.field.validate, getValues)
          }
        }
      });
      isTouched = fieldState?.isTouched;
      isDirty = fieldState?.isDirty;
      isInvalid = fieldState?.invalid;
      inputProps = field;
      ref = field.ref;
    }

    if ((inputProps as any).ref) {
      // needed to fix react warning regarding refs.. ref is passed via inputRef property anyway
      delete (inputProps as any).ref;
    }

    return (
      <Component
        {...inputProps}
        {...(isTouched ? { isTouched: isTouched } : {})}
        {...(isDirty ? { isDirty: isDirty } : {})}
        {...(isInvalid ? { isInvalid: isInvalid } : {})}
        inputRef={ref}
        {...props}
        disabled={result.disabled === true || props.field.disabled === true}
      />
    );
  };

  component.displayName = "WithRegisterFieldHoc";

  return component;
}

const addValidations = (
  validations: IValidation[] | undefined,
  addValidations: IValidation[] | undefined
) => {
  return [...(validations ?? []), ...(addValidations ?? [])];
};

const removeValidations = (
  validations: IValidation[] | undefined,
  removeValidations: IValidation[] | undefined
) => {
  return validations?.filter(
    (item) => !removeValidations?.some((removeValidation) => removeValidation.key === item.key)
  );
};
